import React, { useState, useEffect } from 'react';
import { Store, ArrowLeft } from 'lucide-react';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://check.policypulsehq.com/api';

export function StoreSelector({ platform, onBack, onSelectStore }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStores();
  }, [platform]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let endpoint = '';
      if (platform === 'amazon') {
        endpoint = '/amazon/stores';
      } else if (platform === 'ebay') {
        endpoint = '/ebay/stores';
      }
      
      if (endpoint) {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        setStores(data.stores || []);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformColor = () => {
    switch (platform) {
      case 'amazon':
        return 'from-orange-500 to-yellow-500';
      case 'ebay':
        return 'from-blue-500 to-indigo-500';
      case 'shopify':
        return 'from-green-500 to-emerald-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getPlatformName = () => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900">
        <div className="text-white text-xl">Loading stores...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 p-4">
      <div className="w-full max-w-6xl mx-auto py-12">
        <button
          onClick={onBack}
          className="flex items-center text-white mb-8 hover:text-gray-300 transition-colors"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to platforms
        </button>

        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">
            Select {getPlatformName()} Store
          </h1>
          <p className="text-xl text-gray-300">
            Choose a store to manage
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map((store, index) => (
            <button
              key={store.id}
              onClick={() => onSelectStore(store)}
              className="group relative bg-white rounded-xl p-6 shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${getPlatformColor()} opacity-0 group-hover:opacity-10 rounded-xl transition-opacity duration-300`}></div>
              
              <div className="relative">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${getPlatformColor()} rounded-lg mb-4 shadow-lg`}>
                  <Store className="text-white" size={32} />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {store.store_name || `Store ${index + 1}`}
                </h2>
                
                {store.store_description && (
                  <p className="text-gray-600 mb-4 text-sm">
                    {store.store_description}
                  </p>
                )}
                
                <div className={`inline-flex items-center text-transparent bg-gradient-to-r ${getPlatformColor()} bg-clip-text font-semibold group-hover:gap-3 gap-2 transition-all`}>
                  Open Store
                  <span className="transform group-hover:translate-x-1 transition-transform">→</span>
                </div>

                {store.is_active === false && (
                  <div className="mt-4">
                    <span className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                      Inactive
                    </span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {stores.length === 0 && (
          <div className="text-center text-white mt-12">
            <p className="text-xl mb-4">No stores configured yet</p>
            <p className="text-gray-400">Please contact your administrator to set up stores</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default StoreSelector;